import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { sendChat, ChatApiError } from "@/lib/sentinelChatApi";

const ChatContext = createContext(null);

const STORAGE_MESSAGES = "sentinel.chatMessages";
const STORAGE_SESSION = "sentinel.chatSession";

function loadFromStorage(key, fallback) {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw);
  } catch (e) {
    return fallback;
  }
}

function saveToStorage(key, value) {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch (e) {
    // ignore
  }
}

export function ChatProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(() => loadFromStorage(STORAGE_MESSAGES, []));
  const [session, setSession] = useState(() => loadFromStorage(STORAGE_SESSION, {}));
  const [analysis, setAnalysis] = useState(null);
  const [isThinking, setIsThinking] = useState(false);
  const [error, setError] = useState(null);
  const [pendingPrefill, setPendingPrefill] = useState(null);
  const abortRef = useRef(null);
  const didMountMessagesRef = useRef(false);
  const didMountSessionRef = useRef(false);

  useEffect(() => {
    if (!didMountMessagesRef.current) {
      didMountMessagesRef.current = true;
      return;
    }
    saveToStorage(STORAGE_MESSAGES, messages);
  }, [messages]);

  useEffect(() => {
    if (!didMountSessionRef.current) {
      didMountSessionRef.current = true;
      return;
    }
    saveToStorage(STORAGE_SESSION, session);
  }, [session]);

  const openChat = useCallback(({ prefill } = {}) => {
    setOpen(true);
    if (prefill) setPendingPrefill(prefill);
  }, []);

  const closeChat = useCallback(() => setOpen(false), []);

  const clearChat = useCallback(() => {
    setMessages([]);
    setSession({});
    setError(null);
  }, []);

  const consumePrefill = useCallback(() => {
    const p = pendingPrefill;
    setPendingPrefill(null);
    return p;
  }, [pendingPrefill]);

  const sendMessage = useCallback(
    async (content) => {
      const trimmed = String(content || "").trim();
      if (!trimmed || isThinking) return;
      setError(null);

      const next = [...messages, { role: "user", content: trimmed }];
      setMessages(next);
      setIsThinking(true);

      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const enrichedSession = analysis
          ? { ...session, analysis }
          : session;
        const { reply, session: nextSession } = await sendChat(next, enrichedSession, {
          signal: controller.signal,
        });
        setMessages((m) => [...m, { role: "assistant", content: reply }]);
        setSession(nextSession || {});
      } catch (err) {
        if (err instanceof ChatApiError && err.code === "aborted") return;
        setError(err instanceof ChatApiError ? err.code : "network");
      } finally {
        setIsThinking(false);
        abortRef.current = null;
      }
    },
    [messages, session, isThinking, analysis]
  );

  const cancelInflight = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  const value = useMemo(
    () => ({
      open,
      openChat,
      closeChat,
      messages,
      session,
      analysis,
      setAnalysis,
      isThinking,
      error,
      sendMessage,
      cancelInflight,
      clearChat,
      pendingPrefill,
      consumePrefill,
    }),
    [
      open,
      openChat,
      closeChat,
      messages,
      session,
      analysis,
      isThinking,
      error,
      sendMessage,
      cancelInflight,
      clearChat,
      pendingPrefill,
      consumePrefill,
    ]
  );

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
}

export function useChat() {
  const ctx = useContext(ChatContext);
  if (!ctx) throw new Error("useChat must be used within a ChatProvider");
  return ctx;
}
